// src/components/HealthCheck.tsx

import { useState, useEffect } from 'react';
import type { ExternalApiStatus } from '../types';
import apiService from '../services/api';

interface HealthCheckItem {
  name: string;
  status: 'healthy' | 'warning' | 'unhealthy';
  message: string;
}

function HealthCheck() {
  const [overallStatus, setOverallStatus] = useState<'healthy' | 'degraded' | 'unhealthy'>('healthy');
  const [checks, setChecks] = useState<HealthCheckItem[]>([]);
  const [externalApis, setExternalApis] = useState<ExternalApiStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastChecked, setLastChecked] = useState(new Date());

  useEffect(() => {
    performHealthCheck();
    const interval = setInterval(performHealthCheck, 30000); // Check every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const performHealthCheck = async () => {
    try {
      setLoading(true);
      setError('');

      const [healthData, apiStatusData] = await Promise.all([
        apiService.getDetailedHealth(),
        apiService.getExternalApiStatus(),
      ]);

      setOverallStatus(healthData.status);
      setChecks(healthData.checks || []);
      setExternalApis(apiStatusData);
      setLastChecked(new Date());
    } catch (err: any) {
      console.error('Health check error:', err);
      setError('Failed to perform health check. Make sure IpInfo.Api is running.');
      setOverallStatus('unhealthy');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
      case 'online':
        return '✅';
      case 'warning':
        return '⚠️';
      case 'unhealthy':
      case 'offline':
        return '❌';
      default:
        return '❓';
    }
  };

  if (loading && checks.length === 0) {
    return (
      <div>
        <div className="card">
          <p className="loading">Performing health check...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="card">
        <div className="card-header">
          <h2>System Health Check</h2>
          <button onClick={performHealthCheck} disabled={loading} className="btn btn-secondary">
            {loading ? 'Checking...' : '🔄 Refresh'}
          </button>
        </div>
        
        <div className="result-section">
          <h3>Overall Status: 
            <span className={`result-value ${overallStatus}`}>
              {getStatusIcon(overallStatus)} {overallStatus.toUpperCase()}
            </span>
          </h3>
          <p style={{ color: '#718096', fontSize: '0.875rem' }}>
            Last checked: {lastChecked.toLocaleTimeString()}
          </p>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}
      </div>

      <div className="card">
        <h2>Component Health</h2>
        <div className="health-grid">
          {checks.map((check, index) => (
            <div key={index} className={`health-item ${check.status}`}>
              <div>
                <div className="health-item-name">
                  <span className="health-icon">{getStatusIcon(check.status)}</span>
                  {' '}{check.name}
                </div>
                <div className="health-item-message">{check.message}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="card">
        <h2>External API Status</h2>
        <div className="health-grid">
          {externalApis.map((api, index) => (
            <div key={index} className={`health-item ${api.status}`}>
              <div>
                <div className="health-item-name">
                  <span className="health-icon">{getStatusIcon(api.status)}</span>
                  {' '}{api.name}
                </div>
                <div className="health-item-message">
                  Status: {api.status.toUpperCase()} | Response: {api.responseTime}ms
                  <br />
                  Last check: {new Date(api.lastCheck).toLocaleTimeString()}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default HealthCheck;